import { useSyncExternalStore } from 'react';
import { PLAIN, OPERATING_FLOW } from '../copy/plainLanguage';
import { PLAIN_EN, OPERATING_FLOW_EN } from '../copy/plainLanguage.en';
import { INTEGRATION_MODULES } from '../copy/integrationModules';
import { INTEGRATION_MODULES_EN } from '../copy/integrationModules.en';
import { INLINE_COPY, TOAST_COPY } from '../feedback/toastCopy';
import { INLINE_EN, TOAST_EN } from '../copy/toastCopy.en';

export type Locale = 'ko' | 'en';

const STORAGE_KEY = 'pw_locale';

export function parseLocale(raw: string | null | undefined): Locale | null {
  if (!raw) return null;
  const s = raw.trim().toLowerCase();
  if (s === 'en' || s.startsWith('en-')) return 'en';
  if (s === 'ko' || s.startsWith('ko-')) return 'ko';
  return null;
}

export function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'ko';
  const fromQuery = parseLocale(new URLSearchParams(window.location.search).get('lang'));
  if (fromQuery) return fromQuery;
  try {
    const stored = parseLocale(window.localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch {
    /* private mode */
  }
  return 'ko';
}

let current: Locale = detectInitialLocale();
const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return current;
}

export function applyDocumentMeta(locale: Locale = current): void {
  if (typeof document === 'undefined') return;
  const plain = locale === 'en' ? PLAIN_EN : PLAIN;
  document.documentElement.lang = locale;
  document.title = plain.chrome.documentTitle;
  const meta = document.querySelector('meta[name="description"]');
  if (meta) meta.setAttribute('content', plain.chrome.metaDescription);
}

export function setLocale(next: Locale): void {
  const changed = next !== current;
  current = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
  applyDocumentMeta(next);
  if (changed) listeners.forEach((fn) => fn());
}

export function subscribeLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useLocale(): Locale {
  return useSyncExternalStore(subscribeLocale, getLocale, () => 'ko');
}

export function getPlain(locale: Locale = current) {
  return locale === 'en' ? PLAIN_EN : PLAIN;
}

export function getOperatingFlow(locale: Locale = current) {
  return locale === 'en' ? OPERATING_FLOW_EN : OPERATING_FLOW;
}

export function getToastCopy(locale: Locale = current) {
  return locale === 'en' ? TOAST_EN : TOAST_COPY;
}

export function getInlineCopy(locale: Locale = current) {
  return locale === 'en' ? INLINE_EN : INLINE_COPY;
}

export function getIntegrationModules(locale: Locale = current) {
  return locale === 'en' ? INTEGRATION_MODULES_EN : INTEGRATION_MODULES;
}

export function usePlain() {
  return getPlain(useLocale());
}

export function useOperatingFlow() {
  return getOperatingFlow(useLocale());
}

export function useToastCopy() {
  return getToastCopy(useLocale());
}

export function useInlineCopy() {
  return getInlineCopy(useLocale());
}

export function useIntegrationModules() {
  return getIntegrationModules(useLocale());
}

export function antdLocaleCode(locale: Locale): string {
  return locale === 'en' ? 'en-US' : 'ko-KR';
}
